import { inject, Injectable } from '@angular/core';
import { getIdToken } from 'firebase/auth';
import { firstValueFrom, take } from 'rxjs';
import { AUTH } from '../firebase/firebase.tokens';
import { authState$ } from '../firebase/auth-state';
import { PaymentFailedError } from './payment-failed.error';
import { ApiError, parseRetryAfterMs } from './api.error';
import { environment } from 'src/environments/environment';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type ApiBody = unknown;

@Injectable({
  providedIn: 'root',
})
/**
 * Thin wrapper around the Fetch API that adds authentication,
 * content-type negotiation, and standard error handling for
 * all calls to the Enrollhere v1 REST API.
 */
export class EnrollhereApiService {
  private readonly auth = inject(AUTH);
  private baseUrl = `${(environment as { api?: { endpoint?: string } })?.api?.endpoint ?? ''}/v1`;
  /**
   * 30s in deployed environments. A local API talking to production data pays a
   * cross-region round trip per call and fans out across every agency, so the
   * local environment file raises this; deployed builds leave it unset.
   */
  private readonly defaultTimeoutMs =
    (environment as { apiTimeoutMs?: number })?.apiTimeoutMs ?? 30_000;

  /**
   * `currentUser` is null both when signed out and while Firebase is still
   * restoring the session from IndexedDB, so it cannot be read synchronously
   * to mean "signed out" — a call racing the restore would reject a reader who
   * is in fact signed in. Only a settled answer decides. A warm session
   * resolves without opening an observer, since that callback is async and can
   * stall during a token refresh.
   */
  private async resolveIdToken(): Promise<string> {
    const user =
      this.auth.currentUser ??
      (await firstValueFrom(authState$(this.auth).pipe(take(1))));
    if (!user) {
      throw new Error('EnrollhereApiService: not signed in');
    }
    return getIdToken(user);
  }

  private withTimeout(
    signal?: AbortSignal,
    timeoutMs = this.defaultTimeoutMs
  ): AbortSignal | undefined {
    if (
      typeof AbortSignal === 'undefined' ||
      typeof AbortSignal.timeout !== 'function'
    ) {
      return signal;
    }
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    if (!signal) {
      return timeoutSignal;
    }
    if (typeof AbortSignal.any === 'function') {
      return AbortSignal.any([signal, timeoutSignal]);
    }
    return signal;
  }

  /** Resolves a relative API path to a fully-qualified URL. */
  private buildUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${this.baseUrl}/${cleanPath}`;
  }

  buildFullUrl(path: string): string {
    return this.buildUrl(path);
  }

  /**
   * Maps non-OK fetch responses to the shared API error contract:
   * 402 -> PaymentFailedError so global billing UX can react,
   * anything else !ok -> ApiError carrying status/statusText/body.
   * Use this for non-JSON endpoints (e.g. binary downloads) that
   * bypass makeRequest but must keep the same error semantics.
   */
  async throwIfNotOk(response: Response): Promise<void> {
    if (response.status === 402) {
      throw new PaymentFailedError();
    }
    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new ApiError(
        response.status,
        response.statusText,
        errorBody,
        parseRetryAfterMs(response.headers?.get('Retry-After'))
      );
    }
  }

  /** Type guard for bodies that should not receive a Content-Type header. */
  private isFormLike(body: unknown): body is FormData | Blob {
    return body instanceof FormData || body instanceof Blob;
  }

  /**
   * Executes a fetch with an injected Bearer token.
   * Throws {@link PaymentFailedError} on 402 and
   * {@link ApiError} on any other non-OK status.
   */
  private async authenticatedFetch(
    url: string,
    init: RequestInit,
    timeoutMs?: number
  ): Promise<Response> {
    const token = await this.resolveIdToken();
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(url, {
      ...init,
      headers,
      signal: this.withTimeout(init.signal ?? undefined, timeoutMs),
    });

    await this.throwIfNotOk(response);

    return response;
  }

  private async parseJsonResponse<T>(response: Response): Promise<T> {
    const text = await response.clone().text();
    if (!text.trim()) {
      return {} as T;
    }
    return response.json();
  }

  /**
   * Normalizes a request body the same way for JSON and binary endpoints:
   * URLSearchParams / FormData / Blob / string pass through; other values
   * are JSON-serialized with Content-Type application/json.
   */
  private prepareRequestBody(
    body: ApiBody | undefined,
    headers: Headers | Record<string, string>
  ): BodyInit | undefined {
    const setHeader = (name: string, value: string): void => {
      if (headers instanceof Headers) {
        if (!headers.has(name)) {
          headers.set(name, value);
        }
        return;
      }
      if (!headers[name]) {
        headers[name] = value;
      }
    };

    if (body === undefined) {
      return undefined;
    }
    if (body instanceof URLSearchParams) {
      setHeader(
        'Content-Type',
        'application/x-www-form-urlencoded;charset=UTF-8'
      );
      return body;
    }
    if (this.isFormLike(body)) {
      if (body instanceof Blob && body.type) {
        setHeader('Content-Type', body.type);
      }
      return body;
    }
    if (typeof body === 'string') {
      setHeader('Content-Type', 'application/json');
      return body;
    }
    setHeader('Content-Type', 'application/json');
    return JSON.stringify(body);
  }

  /**
   * Builds and sends an authenticated JSON request, inferring
   * Content-Type from the body type and parsing the JSON response.
   */
  private async makeRequest<T>(
    path: string,
    method: HttpMethod,
    body?: ApiBody,
    additionalHeaders?: Record<string, string>,
    signal?: AbortSignal,
    timeoutMs?: number
  ): Promise<T> {
    const url = this.buildUrl(path);

    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...additionalHeaders,
    };

    const requestBody = this.prepareRequestBody(body, headers);

    const response = await this.authenticatedFetch(
      url,
      {
        method,
        headers,
        body: requestBody,
        signal,
      },
      timeoutMs
    );

    return this.parseJsonResponse<T>(response);
  }

  /** Sends an authenticated GET request and returns the parsed JSON body. */
  async get<T = any>(path: string, signal?: AbortSignal): Promise<T> {
    return this.makeRequest<T>(path, 'GET', undefined, undefined, signal);
  }

  /**
   * Like {@link get}, but accepts a fully-qualified URL instead of a
   * relative path.  Useful for backup/failover hosts whose base URL
   * differs from the primary endpoint.
   */
  async getFromUrl<T = any>(url: string): Promise<T> {
    const response = await this.authenticatedFetch(url, {
      method: 'GET',
      headers: new Headers({ Accept: 'application/json' }),
    });

    return this.parseJsonResponse<T>(response);
  }

  /**
   * Authenticated GET returning a Blob plus headers.
   * Pass `timeoutMs` when the body can outlast the default abort.
   */
  async getBlob(
    path: string,
    accept = 'application/octet-stream',
    timeoutMs?: number
  ): Promise<{ blob: Blob; headers: Headers }> {
    const response = await this.authenticatedFetch(
      this.buildUrl(path),
      {
        method: 'GET',
        headers: new Headers({ Accept: accept }),
      },
      timeoutMs
    );
    return { blob: await response.blob(), headers: response.headers };
  }

  /**
   * JSON request with no Firebase session. Never attaches Authorization —
   * even if a user is signed in — so public invite/login routes stay
   * anonymous. Same error mapping as authenticated methods.
   */
  private async makeUnauthenticatedRequest<T>(
    path: string,
    method: HttpMethod,
    body?: ApiBody,
    additionalHeaders?: Record<string, string>,
    signal?: AbortSignal
  ): Promise<T> {
    const url = this.buildUrl(path);
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...additionalHeaders,
    };
    const requestBody = this.prepareRequestBody(body, headers);
    const response = await fetch(url, {
      method,
      headers,
      body: requestBody,
      signal: this.withTimeout(signal),
    });
    await this.throwIfNotOk(response);
    return this.parseJsonResponse<T>(response);
  }

  /**
   * Sends an unauthenticated GET. Used for public invite-status and similar
   * token-in-URL reads that must not require a signed-in user.
   */
  async getUnauthenticated<T = any>(
    path: string,
    additionalHeaders?: Record<string, string>,
    signal?: AbortSignal
  ): Promise<T> {
    return this.makeUnauthenticatedRequest<T>(
      path,
      'GET',
      undefined,
      additionalHeaders,
      signal
    );
  }

  /**
   * Sends an unauthenticated POST. Used for public login flows and
   * token-gated writes that must not attach a Bearer token.
   */
  async postUnauthenticated<T = any>(
    path: string,
    body?: ApiBody,
    additionalHeaders?: Record<string, string>,
    signal?: AbortSignal
  ): Promise<T> {
    return this.makeUnauthenticatedRequest<T>(
      path,
      'POST',
      body,
      additionalHeaders,
      signal
    );
  }

  /**
   * Sends an authenticated POST request and returns the parsed JSON body.
   * Pass an {@link AbortSignal} to cancel an in-flight request (e.g. a
   * debounced caller superseding its own prior request). Pass `timeoutMs` to
   * override the default 30s ceiling for requests that are expected to run
   * longer (e.g. a bulk action over hundreds of entities) — the default is
   * sized for ordinary single-entity requests and would otherwise abort a
   * legitimately slow bulk write client-side while the server keeps going.
   */
  async post<T = any>(
    path: string,
    body?: ApiBody,
    additionalHeaders?: Record<string, string>,
    signal?: AbortSignal,
    timeoutMs?: number
  ): Promise<T> {
    return this.makeRequest<T>(
      path,
      'POST',
      body,
      additionalHeaders,
      signal,
      timeoutMs
    );
  }

  /**
   * Sends an authenticated POST and returns the response as a Blob plus
   * headers (e.g. Content-Disposition for file downloads). Body handling
   * matches {@link post} / {@link makeRequest} — JSON objects are
   * stringified, while FormData/Blob/URLSearchParams pass through.
   */
  async postBlob(
    path: string,
    body?: ApiBody,
    accept = 'application/octet-stream'
  ): Promise<{ blob: Blob; headers: Headers }> {
    const headers = new Headers({ Accept: accept });
    const requestBody = this.prepareRequestBody(body, headers);
    const response = await this.authenticatedFetch(this.buildUrl(path), {
      method: 'POST',
      headers,
      body: requestBody,
    });
    return { blob: await response.blob(), headers: response.headers };
  }

  /** Sends an authenticated PUT request and returns the parsed JSON body. */
  async put<T = any>(path: string, body?: ApiBody): Promise<T> {
    return this.makeRequest<T>(path, 'PUT', body);
  }

  /** Sends an authenticated PATCH request and returns the parsed JSON body. */
  async patch<T = any>(path: string, body?: ApiBody): Promise<T> {
    return this.makeRequest<T>(path, 'PATCH', body);
  }

  /**
   * Sends an authenticated DELETE request and returns the parsed JSON body.
   * `body` is for endpoints that take one (e.g. a typed confirmation); omit
   * it otherwise, so no JSON Content-Type goes out on an empty request.
   */
  async delete<T = any>(path: string, body?: ApiBody): Promise<T> {
    return this.makeRequest<T>(path, 'DELETE', body);
  }
}
